import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  BarChart3, 
  Download, 
  FileText, 
  FileSpreadsheet, 
  Users, 
  Calendar,
  Clock,
  DollarSign,
  TrendingUp,
  Filter,
  ChevronDown,
  Loader2,
  FileDown,
  Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent, 
  DropdownMenuItem 
} from '@/components/ui/dropdown-menu';

export default function Reports() {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [selectedReport, setSelectedReport] = useState('employees');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  // Iter67 — controlled state for the Leave Status filter so the value
  // can't go stale on re-render and so the Leave Report card can reflect
  // the active filter inline (was previously uncontrolled via getElementById).
  const [leaveStatus, setLeaveStatus] = useState('all');
  // Iter69 — Department filter that scopes ALL six report cards. Default
  // 'all' = no department filter. Department list is pulled from the
  // employees collection (unique non-empty department values) so it
  // automatically reflects the org structure.
  const [department, setDepartment] = useState('all');
  const [departmentOptions, setDepartmentOptions] = useState([]);
  
  // Report data states
  const [claimsData, setClaimsData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [leaveData, setLeaveData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);

  const reportTypes = [
    { id: 'employees', name: 'Employees', icon: Users, color: 'blue' },
    { id: 'payroll', name: 'Payroll', icon: DollarSign, color: 'emerald' },
    { id: 'claims', name: 'Claims', icon: FileText, color: 'purple' },
    { id: 'attendance', name: 'Attendance', icon: Clock, color: 'orange' },
    { id: 'leave', name: 'Leave', icon: Calendar, color: 'pink' },
    { id: 'performance', name: 'Performance', icon: TrendingUp, color: 'teal' },
  ];

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = [2023, 2024, 2025, 2026];

  // Iter69 — fetch unique department names once on mount so the
  // Department dropdown reflects the live org structure. Filters out
  // empties / null values.
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await axios.get(`${API}/employees`);
        const depts = [...new Set(
          (res.data || [])
            .map((e) => e.department)
            .filter((d) => d && String(d).trim())
        )].sort();
        setDepartmentOptions(depts);
      } catch (e) {
        // Non-fatal — dropdown just shows "All Departments" + nothing else.
        console.error('Failed to load departments for Reports filter', e);
      }
    };
    fetchDepartments();
  }, []);

  // Iter68 — auto-refresh any preview that's already been opened when the
  // user changes Month / Year / Leave Status. Iter69 — now also reacts to
  // the Department filter for every preview type.
  useEffect(() => {
    if (leaveData) fetchReportData('leave', { silent: true });
  }, [leaveStatus, month, year, department]);

  useEffect(() => {
    if (claimsData) fetchReportData('claims', { silent: true });
    if (attendanceData) fetchReportData('attendance', { silent: true });
  }, [month, year, department]);

  useEffect(() => {
    if (performanceData) fetchReportData('performance', { silent: true });
  }, [year, department]);

  const fetchReportData = async (reportType, { silent = false } = {}) => {
    setLoading(true);
    try {
      let url = '';
      // Iter69 — append the optional department scope to every report URL.
      const deptParam = department && department !== 'all' ? `&department=${encodeURIComponent(department)}` : '';
      switch (reportType) {
        case 'claims':
          url = `${API}/reports/claims?month=${month}&year=${year}${deptParam}`;
          const claimsRes = await axios.get(url);
          setClaimsData(claimsRes.data);
          break;
        case 'attendance':
          url = `${API}/reports/attendance?month=${month}&year=${year}${deptParam}`;
          const attendanceRes = await axios.get(url);
          setAttendanceData(attendanceRes.data);
          break;
        case 'leave':
          url = `${API}/reports/leave?year=${year}&month=${month}&status=${leaveStatus}${deptParam}`;
          const leaveRes = await axios.get(url);
          setLeaveData(leaveRes.data);
          break;
        case 'performance':
          url = `${API}/reports/performance?year=${year}${deptParam}`;
          const perfRes = await axios.get(url);
          setPerformanceData(perfRes.data);
          break;
        default:
          break;
      }
      if (!silent) toast.success('Report data loaded');
    } catch (error) {
      console.error('Error fetching report:', error);
      if (!silent) toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (reportType, format = 'excel') => {
    setDownloadLoading(`${reportType}-${format}`);
    try {
      let url = '';
      let filename = '';
      const monthName = months.find(m => m.value === month)?.label || 'Unknown';
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      // Iter69 — append department scope to every download URL.
      const deptParam = department && department !== 'all' ? `&department=${encodeURIComponent(department)}` : '';
      const deptSuffix = department && department !== 'all' ? `_${department.replace(/\s+/g, '')}` : '';

      switch (reportType) {
        case 'employees':
          url = `${API}/reports/employees/download?format=${format}${deptParam}`;
          filename = `Employee_Report${deptSuffix}_${new Date().toISOString().split('T')[0]}`;
          break;
        case 'payroll':
          url = `${API}/reports/payroll/download?month=${month}&year=${year}&format=${format}${deptParam}`;
          filename = `Payroll_Report${deptSuffix}_${monthName}_${year}`;
          break;
        case 'claims':
          url = `${API}/reports/claims/download?month=${month}&year=${year}&format=${format}${deptParam}`;
          filename = `Claims_Report${deptSuffix}_${monthName}_${year}`;
          break;
        case 'attendance':
          url = `${API}/reports/attendance/download?month=${month}&year=${year}&format=${format}${deptParam}`;
          filename = `Attendance_Report${deptSuffix}_${monthName}_${year}`;
          break;
        case 'leave':
          url = `${API}/reports/leave/download?year=${year}&month=${month}&status=${leaveStatus}&format=${format}${deptParam}`;
          filename = `Leave_Report_${leaveStatus === 'all' ? 'All' : leaveStatus.charAt(0).toUpperCase() + leaveStatus.slice(1)}${deptSuffix}_${monthName}_${year}`;
          break;
        case 'performance':
          url = `${API}/reports/performance/download?year=${year}&format=${format}${deptParam}`;
          filename = `Performance_Report${deptSuffix}_${year}`;
          break;
        default:
          return;
      }

      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${filename}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloadLoading(null);
    }
  };

  const seedDemoData = async () => {
    // Iter95 — Backend `/seed-demo-data` is now fully idempotent: every call
    // deletes the previous demo snapshot and inserts a fresh one. Warn the
    // tester so they know real data is safe and previous demo data is gone.
    const confirmed = window.confirm(
      "Reseed Demo Data?\n\n" +
      "This will REPLACE any previously-seeded demo employees, claims, " +
      "attendance, leaves, and performance reviews with a fresh consistent " +
      "snapshot.\n\n" +
      "Your real (non-demo) records will NOT be touched.\n\n" +
      "Continue?"
    );
    if (!confirmed) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/seed-demo-data`);
      const { results = {}, cleared = {} } = response.data || {};
      const clearedTotal = Object.values(cleared).reduce((a, b) => a + (b || 0), 0);
      toast.success(
        `Demo data reseeded — ${results.employees || 0} employees, ` +
        `${results.claims || 0} claims, ${results.attendance || 0} attendance, ` +
        `${results.leave || 0} leaves, ${results.performance || 0} reviews. ` +
        (clearedTotal > 0
          ? `(${clearedTotal} prior demo record${clearedTotal === 1 ? '' : 's'} cleared)`
          : '')
      );
    } catch (error) {
      console.error('Seed error:', error);
      toast.error(error?.response?.data?.detail || 'Failed to reseed demo data');
    } finally {
      setLoading(false);
    }
  };

  const getColorClass = (color) => {
    const colors = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
      teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          Reports & Analytics
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Generate and download comprehensive HR reports
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filters:</span>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600 dark:text-slate-400">Month</Label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="h-9 px-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              data-testid="month-filter"
            >
              {months.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600 dark:text-slate-400">Year</Label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="h-9 px-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              data-testid="year-filter"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600 dark:text-slate-400">Leave Status</Label>
            <select
              id="leave-status-filter"
              value={leaveStatus}
              onChange={(e) => setLeaveStatus(e.target.value)}
              className="h-9 px-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              data-testid="leave-status-filter"
            >
              <option value="all">All Status</option>
              <option value="approved">Approved Only</option>
              <option value="pending">Pending Only</option>
              <option value="rejected">Rejected Only</option>
            </select>
          </div>
          {/* Iter69 — Department filter. Scopes ALL six report cards
              (preview + Excel + PDF) via the new ?department= query param
              on every /api/reports/* endpoint. */}
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600 dark:text-slate-400">Department</Label>
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="h-9 px-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm"
              data-testid="department-filter"
            >
              <option value="all">All Departments</option>
              {departmentOptions.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          {department !== 'all' && (
            <button
              type="button"
              onClick={() => setDepartment('all')}
              className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 underline"
              data-testid="clear-department-filter"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Report Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          const isLoading = downloadLoading === report.id;
          
          return (
            <div 
              key={report.id}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`report-card-${report.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`h-12 w-12 rounded-xl ${getColorClass(report.color)} flex items-center justify-center`}>
                  <Icon className="h-6 w-6" />
                </div>
                {['claims', 'attendance', 'leave', 'performance'].includes(report.id) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchReportData(report.id)}
                    disabled={loading}
                    className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    data-testid={`preview-${report.id}-btn`}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview'}
                  </Button>
                )}
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                {report.name} Report
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                {report.id === 'employees' && 'Complete employee directory with details'}
                {report.id === 'payroll' && `Payroll summary for ${months.find(m => m.value === month)?.label} ${year}`}
                {report.id === 'claims' && `Claims analytics for ${months.find(m => m.value === month)?.label} ${year}`}
                {report.id === 'attendance' && `Attendance records for ${months.find(m => m.value === month)?.label} ${year}`}
                {report.id === 'leave' && (() => {
                  const label = leaveStatus === 'all' ? 'All' : leaveStatus.charAt(0).toUpperCase() + leaveStatus.slice(1);
                  return `${label} leave applications for ${months.find(m => m.value === month)?.label} ${year}`;
                })()}
                {report.id === 'performance' && `Performance reviews for ${year}`}
              </p>
              {report.id === 'leave' && (
                <div className="mb-4 flex items-center gap-2 text-xs">
                  <span className="text-slate-500 dark:text-slate-400">Active filter:</span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${
                      leaveStatus === 'pending'
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        : leaveStatus === 'approved'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : leaveStatus === 'rejected'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                    }`}
                    data-testid="leave-report-active-filter-chip"
                  >
                    {leaveStatus === 'all' ? 'All Status' : `${leaveStatus.charAt(0).toUpperCase() + leaveStatus.slice(1)} Only`}
                  </span>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button
                  onClick={() => downloadReport(report.id, 'excel')}
                  disabled={downloadLoading === `${report.id}-excel`}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700"
                  data-testid={`download-${report.id}-excel-btn`}
                >
                  {downloadLoading === `${report.id}-excel` ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                  )}
                  Excel
                </Button>
                <Button
                  onClick={() => downloadReport(report.id, 'pdf')}
                  disabled={downloadLoading === `${report.id}-pdf`}
                  variant="outline"
                  className="flex-1 border-slate-300 dark:border-slate-600"
                  data-testid={`download-${report.id}-pdf-btn`}
                >
                  {downloadLoading === `${report.id}-pdf` ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileDown className="h-4 w-4 mr-2" />
                  )}
                  PDF
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Seed Demo Data Button */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">Demo Data</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Reseeds a fresh, idempotent snapshot of demo employees/claims/attendance/leaves for testing. Replaces prior demo data — real records are never touched.
            </p>
          </div>
          <Button
            onClick={seedDemoData}
            disabled={loading}
            variant="outline"
            size="sm"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
            data-testid="seed-demo-data-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
            Reseed Demo Data
          </Button>
        </div>
      </div>

      {/* Analytics Preview Sections */}
      {claimsData && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="claims-preview">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-500" />
              Claims Analytics Preview
            </h3>
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span>Showing:</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                {months.find(m => m.value === month)?.label} {year}
              </span>
              {department !== 'all' && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    {department}
                  </span>
                </>
              )}
            </div>
          </div>
          {/* Iter70 — empty-state hint when zero claims match the current
              filter. Without this, testers saw "Total Claims: 0" and
              thought the feature was broken when actually no claims fall
              inside the selected month/year/department. */}
          {(!claimsData.total_claims || claimsData.total_claims === 0) && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200" data-testid="claims-preview-empty-hint">
              No claims found for <strong>{months.find(m => m.value === month)?.label} {year}</strong>{department !== 'all' ? <> in <strong>{department}</strong></> : null}. Try changing the Month / Year{department !== 'all' ? ' or Department' : ''} filter above.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Claims</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{claimsData.total_claims}</p>
            </div>
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Approved</p>
              <p className="text-2xl font-bold text-emerald-600">{claimsData.approved_count}</p>
              <p className="text-xs text-emerald-600">RM {claimsData.approved_amount?.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg" title="Includes claims in mid-workflow statuses (pending, hr_checked, submitted, under_review)">
              <p className="text-xs text-slate-500 dark:text-slate-400">Pending</p>
              <p className="text-2xl font-bold text-amber-600" data-testid="claims-pending">{claimsData.pending_count}</p>
              <p className="text-xs text-amber-600">RM {claimsData.pending_amount?.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{claimsData.rejected_count}</p>
            </div>
          </div>
          {claimsData.by_type && Object.keys(claimsData.by_type).length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">By Type</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(claimsData.by_type).map(([type, data]) => (
                  <div key={type} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 rounded-full text-sm text-purple-700 dark:text-purple-300">
                    {type.replace('_', ' ')}: {data.count} (RM {data.amount?.toLocaleString()})
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {attendanceData && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="attendance-preview">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              Attendance Analytics Preview
            </h3>
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span>Showing:</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                {months.find(m => m.value === month)?.label} {year}
              </span>
              {department !== 'all' && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">{department}</span>
                </>
              )}
            </div>
          </div>
          {(!attendanceData.total_records || attendanceData.total_records === 0) && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200" data-testid="attendance-preview-empty-hint">
              No attendance records found for <strong>{months.find(m => m.value === month)?.label} {year}</strong>{department !== 'all' ? <> in <strong>{department}</strong></> : null}. Try changing the Month / Year{department !== 'all' ? ' or Department' : ''} filter above.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Records</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="attn-total">{attendanceData.total_records}</p>
            </div>
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Present</p>
              <p className="text-2xl font-bold text-emerald-600" data-testid="attn-present">{attendanceData.present}</p>
            </div>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Late</p>
              <p className="text-2xl font-bold text-amber-600" data-testid="attn-late">{attendanceData.late}</p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Absent</p>
              <p className="text-2xl font-bold text-red-600" data-testid="attn-absent">{attendanceData.absent}</p>
            </div>
            {/* Iter45 (Ankur 22-Jun-2026) — Half-Day was returned by the
                backend but hidden on the card, breaking the sum vs Total.  */}
            <div className="p-4 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Half-Day</p>
              <p className="text-2xl font-bold text-purple-600" data-testid="attn-half-day">{attendanceData.half_day || 0}</p>
            </div>
            <div className="p-4 bg-pink-50 dark:bg-pink-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">On Leave</p>
              <p className="text-2xl font-bold text-pink-600" data-testid="attn-on-leave">{attendanceData.on_leave || 0}</p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Attendance Rate</p>
              <p className="text-2xl font-bold text-blue-600" data-testid="attn-rate">{attendanceData.attendance_rate}%</p>
            </div>
          </div>
        </div>
      )}

      {leaveData && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="leave-preview">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-pink-500" />
              Leave Analytics Preview
            </h3>
            {/* Iter68 — explicit "Showing" label so the user can verify the
                preview reflects the currently-selected filters. Updates
                instantly via the auto-refresh useEffect. */}
            <div
              className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400"
              data-testid="leave-preview-active-filter"
            >
              <span>Showing:</span>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${
                  leaveStatus === 'pending'
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    : leaveStatus === 'approved'
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : leaveStatus === 'rejected'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                }`}
              >
                {leaveStatus === 'all' ? 'All Status' : `${leaveStatus.charAt(0).toUpperCase() + leaveStatus.slice(1)} Only`}
              </span>
              <span>·</span>
              <span>{months.find(m => m.value === month)?.label} {year}</span>
              {/* Iter69 — explicit department scope in the Leave preview header */}
              {department !== 'all' && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    {department}
                  </span>
                </>
              )}
            </div>
          </div>
          {(!leaveData.total_applications || leaveData.total_applications === 0) && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200" data-testid="leave-preview-empty-hint">
              No leave applications found for <strong>{months.find(m => m.value === month)?.label} {year}</strong>{leaveStatus !== 'all' ? <> with status <strong>{leaveStatus}</strong></> : null}{department !== 'all' ? <> in <strong>{department}</strong></> : null}. Try changing the filters above.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Applications</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="leave-total">{leaveData.total_applications}</p>
            </div>
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Approved</p>
              <p className="text-2xl font-bold text-emerald-600" data-testid="leave-approved">{leaveData.approved_count}</p>
            </div>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Pending</p>
              <p className="text-2xl font-bold text-amber-600" data-testid="leave-pending">{leaveData.pending_count}</p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Rejected</p>
              <p className="text-2xl font-bold text-red-600" data-testid="leave-rejected">{leaveData.rejected_count}</p>
            </div>
            {/* Iter45 (Ankur 22-Jun-2026) — surface Cancelled / Auto-Cancelled
                so Total = Approved + Pending + Rejected + Cancelled and the
                preview reconciles with the Leave module's listing.   */}
            <div className="p-4 bg-slate-100 dark:bg-slate-700/60 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Cancelled</p>
              <p className="text-2xl font-bold text-slate-600 dark:text-slate-300" data-testid="leave-cancelled">{leaveData.cancelled_count || 0}</p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Days Taken</p>
              <p className="text-2xl font-bold text-blue-600" data-testid="leave-days">{leaveData.total_days_taken}</p>
            </div>
          </div>
          {leaveData.by_type && Object.keys(leaveData.by_type).length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">By Leave Type</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(leaveData.by_type).map(([type, data]) => (
                  <div key={type} className="px-3 py-1 bg-pink-100 dark:bg-pink-900/30 rounded-full text-sm text-pink-700 dark:text-pink-300">
                    {type}: {data.count} ({data.days} days)
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {performanceData && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm" data-testid="performance-preview">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-teal-500" />
            Performance Analytics Preview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Reviews</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{performanceData.total_reviews}</p>
            </div>
            <div className="p-4 bg-teal-50 dark:bg-teal-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Average Rating</p>
              <p className="text-2xl font-bold text-teal-600">{performanceData.average_rating}/5</p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400">Rating Distribution</p>
              <div className="flex gap-1 mt-1">
                {[5, 4, 3, 2, 1].map(rating => (
                  <div key={rating} className="text-xs text-center">
                    <span className="text-slate-600 dark:text-slate-400">{rating}★</span>
                    <p className="font-bold text-slate-900 dark:text-slate-100">{performanceData.rating_distribution?.[rating] || 0}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {performanceData.by_department && Object.keys(performanceData.by_department).length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">By Department</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(performanceData.by_department).map(([dept, data]) => (
                  <div key={dept} className="px-3 py-1 bg-teal-100 dark:bg-teal-900/30 rounded-full text-sm text-teal-700 dark:text-teal-300">
                    {dept}: {data.count} reviews (Avg: {data.avg_rating || 'N/A'})
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!claimsData && !attendanceData && !leaveData && !performanceData && (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <BarChart3 className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">
            Click "Preview" on any report card to see analytics, or download directly
          </p>
        </div>
      )}
    </div>
  );
}
